import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import { formatNumber, numberWithCommas } from "../../utils";
import { QrChannelSummary } from "../../types/qr-analytics.type";
import ChangeBadge from "../Dashboard/Ho/ChangeBadge";
import {
  CASH_CHANNEL,
  CREDIT_CHANNEL,
  DIGITAL_CHANNELS,
  DIGITAL_GROUP,
} from "./channel";

interface ChannelSummaryProps {
  readonly totals: QrChannelSummary;
  /** Signed change in the เงินโอน+QR share against the preceding window of equal length. */
  readonly digitalPercentDelta: number;
  readonly previousLabel: string;
}

const TILE_BORDER = "1px solid #E4E6EB";

/**
 * The headline: how many bills the app issued, and how that population splits into the two
 * groups the owner reads — เงินสด against เงินโอน+QR — with the group then broken down into
 * เงินโอน and QR. "จากแอป" is in the label rather than left implicit — the denominator is PWA
 * bills only, and calling it ขายสด would claim the legacy bills that are excluded.
 *
 * Tiles rather than a chart — a handful of values with no time axis are read, not compared
 * visually. The one graphic is the 100% share bar underneath, whose job is the shape of the
 * split at a glance.
 */
export default function ChannelSummary({
  totals,
  digitalPercentDelta,
  previousLabel,
}: ChannelSummaryProps) {
  // เงินเชื่อ should never appear on a cash sale. Shown only when a row proves otherwise,
  // so a data problem surfaces instead of being folded silently into เงินสด.
  const showCredit = totals.credit_count > 0;

  return (
    <Paper sx={{ p: 2 }}>
      <Box
        display="flex"
        alignItems="baseline"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        mb={2}
      >
        <Box display="flex" alignItems="baseline" gap={1.5}>
          <Typography variant="body2" color="text.secondary">
            บิลจากแอปทั้งหมด
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {numberWithCommas(totals.bill_count)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            บิล
          </Typography>
        </Box>
        <Tooltip
          title={`ร้านที่มีบิลในช่วงนี้ ${numberWithCommas(
            totals.shop_count,
          )} ร้าน · จ่ายด้วยเงินโอนหรือ QR อย่างน้อย 1 ครั้ง ${numberWithCommas(
            totals.digital_shop_count,
          )} ร้าน · สแกน QR ${numberWithCommas(totals.qr_shop_count)} ร้าน`}
        >
          <Typography variant="body2" color="text.secondary">
            ร้านที่จ่ายโอน/QR {numberWithCommas(totals.digital_shop_count)}/
            {numberWithCommas(totals.shop_count)} ร้าน (
            {formatNumber(totals.digital_shop_percent)}%)
          </Typography>
        </Tooltip>
      </Box>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Box
            sx={{
              border: TILE_BORDER,
              borderRadius: 1,
              p: 1.5,
              height: "100%",
            }}
          >
            <ChannelLabel
              color={CASH_CHANNEL.color}
              label={CASH_CHANNEL.label}
            />
            <Box display="flex" alignItems="baseline" gap={1}>
              <Typography variant="h6" fontWeight={700}>
                {formatNumber(totals.cash_percent)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {numberWithCommas(totals.cash_count)} บิล
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {formatNumber(totals.cash_amount)} บาท
            </Typography>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Box
            sx={{
              border: "1px solid #C9CCD3",
              borderRadius: 1,
              p: 1.5,
              bgcolor: "#FAFAFB",
              height: "100%",
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Box display="flex" gap="2px">
                {DIGITAL_CHANNELS.map((channel) => (
                  <Swatch key={channel.key} color={channel.color} />
                ))}
              </Box>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ color: DIGITAL_GROUP.color }}
              >
                {DIGITAL_GROUP.label}
              </Typography>
              <Box ml="auto">
                <ChangeBadge
                  percent={digitalPercentDelta}
                  label={previousLabel}
                />
              </Box>
            </Box>
            <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
              <Typography variant="h6" fontWeight={700}>
                {formatNumber(totals.digital_percent)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {numberWithCommas(totals.digital_count)} บิล
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatNumber(totals.digital_amount)} บาท
              </Typography>
            </Box>

            <Grid container spacing={1.5} mt={0.5}>
              {DIGITAL_CHANNELS.map((channel) => {
                const count = totals[channel.countKey] as number;
                const percent = totals[channel.percentKey] as number;
                const amount = totals[channel.amountKey] as string;
                // Share WITHIN the group, so "how much of เงินโอน+QR is QR" reads directly.
                const ofGroup =
                  totals.digital_count > 0
                    ? (count / totals.digital_count) * 100
                    : 0;
                return (
                  <Grid size={{ xs: 12, sm: 6 }} key={channel.key}>
                    <Box
                      sx={{
                        border: TILE_BORDER,
                        borderRadius: 1,
                        p: 1.5,
                        bgcolor: "#fff",
                        height: "100%",
                      }}
                    >
                      <ChannelLabel
                        color={channel.color}
                        label={channel.label}
                      />
                      <Box display="flex" alignItems="baseline" gap={1}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {formatNumber(percent)}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {numberWithCommas(count)} บิล
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {formatNumber(amount)} บาท ·{" "}
                        {formatNumber(ofGroup, { decimals: 1 })}% ของกลุ่ม
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Grid>
      </Grid>

      <ShareBar totals={totals} showCredit={showCredit} />

      {showCredit && (
        <Typography variant="caption" color="text.secondary">
          พบบิลที่บันทึกเป็นเงินเชื่อ {numberWithCommas(totals.credit_count)}{" "}
          บิล ({formatNumber(totals.credit_percent)}%) — ปกติไม่ควรมี
        </Typography>
      )}
    </Paper>
  );
}

function Swatch({ color }: { readonly color: string }) {
  return (
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: "2px",
        bgcolor: color,
        flexShrink: 0,
      }}
    />
  );
}

function ChannelLabel({
  color,
  label,
}: {
  readonly color: string;
  readonly label: string;
}) {
  return (
    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
      <Swatch color={color} />
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

/**
 * The split as one 100%-wide bar, with a bracket row above it naming the two groups.
 *
 * The segments keep the three channel colours; the brackets are what say "these two belong
 * together", so the grouping costs no extra hue. Each bracket is sized from the same percents
 * as the segments under it (plus the 2px gaps it spans). Only a segment wide enough to hold
 * its own label gets one — a number on every segment would be unreadable at 1–2% widths.
 */
function ShareBar({
  totals,
  showCredit,
}: {
  readonly totals: QrChannelSummary;
  readonly showCredit: boolean;
}) {
  const segments = [
    {
      key: CASH_CHANNEL.key,
      label: CASH_CHANNEL.label,
      color: CASH_CHANNEL.color,
      percent: totals.cash_percent,
    },
    ...DIGITAL_CHANNELS.map((channel) => ({
      key: channel.key,
      label: channel.label,
      color: channel.color,
      percent: totals[channel.percentKey] as number,
    })),
    ...(showCredit
      ? [{ ...CREDIT_CHANNEL, percent: totals.credit_percent }]
      : []),
  ].filter((segment) => segment.percent > 0);

  if (segments.length === 0) return null;

  const digitalSegments = segments.filter(
    (segment) => segment.key === "transfer" || segment.key === "qr",
  ).length;
  const groups = [
    {
      key: "cash",
      label: `${CASH_CHANNEL.label} ${formatNumber(totals.cash_percent)}%`,
      color: CASH_CHANNEL.color,
      percent: totals.cash_percent,
      gaps: 0,
    },
    {
      key: "digital",
      label: `${DIGITAL_GROUP.label} ${formatNumber(totals.digital_percent)}%`,
      color: DIGITAL_GROUP.color,
      percent: totals.digital_percent,
      gaps: Math.max(0, digitalSegments - 1),
    },
  ].filter((group) => group.percent > 0);

  return (
    <Box mt={2} mb={1}>
      <Box sx={{ display: "flex", gap: "2px" }} aria-hidden>
        {groups.map((group) => (
          <Box
            key={group.key}
            sx={{
              width: `calc(${group.percent}% + ${group.gaps * 2}px)`,
              minWidth: 2,
              mt: 2.5,
              height: 8,
              border: `1.5px solid ${group.color}`,
              borderBottom: 0,
              borderRadius: "3px 3px 0 0",
              position: "relative",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                bottom: 8,
                left: "50%",
                transform: "translateX(-50%)",
                color: group.color,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {group.label}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box
        sx={{ display: "flex", gap: "2px", height: 28, mt: "3px" }}
        role="img"
        aria-label={[
          ...groups.map((group) => group.label),
          ...segments.map(
            (segment) => `${segment.label} ${formatNumber(segment.percent)}%`,
          ),
        ].join(", ")}
      >
        {segments.map((segment) => (
          <Tooltip
            key={segment.key}
            title={`${segment.label} ${formatNumber(segment.percent)}%`}
          >
            <Box
              sx={{
                width: `${segment.percent}%`,
                bgcolor: segment.color,
                borderRadius: "3px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                minWidth: 2,
              }}
            >
              {segment.percent >= 8 && (
                <Typography
                  variant="caption"
                  sx={{ color: "#fff", fontWeight: 600, whiteSpace: "nowrap" }}
                >
                  {segment.label} {formatNumber(segment.percent)}%
                </Typography>
              )}
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
}
